import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ContactDialog from './ContactDialog';
import { applyRouteSeo } from '../helpers/seoHelper';

const principles = [
  { icon: <InsightsRoundedIcon />, title: 'Start with the outcome', text: 'We understand the business problem first, then choose the simplest technology that can solve it well.' },
  { icon: <CodeRoundedIcon />, title: 'Build for real use', text: 'Every interface, workflow, and integration is shaped around the people who will use it every day.' },
  { icon: <HandshakeRoundedIcon />, title: 'Stay clear and accountable', text: 'Straightforward scope, visible progress, honest recommendations, and support that continues after launch.' },
];

const capabilities = [
  'AI assistants and workflow automation',
  'Focused internal tools and customer portals',
  'Fast, conversion-led business websites',
  'Integrations, deployment, and ongoing improvement',
];

const About = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => applyRouteSeo('/about'), []);

  return (
    <Box sx={{ overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box component="section" sx={{ position: 'relative', bgcolor: '#F7FBFC', pt: { xs: 8, md: 11 }, pb: { xs: 9, md: 12 }, borderBottom: '1px solid rgba(16,42,58,0.08)', '&::before': { content: '""', position: 'absolute', width: 500, height: 500, borderRadius: '50%', top: -290, right: -130, background: 'radial-gradient(circle, rgba(32,184,181,0.15) 0%, rgba(32,184,181,0) 70%)' } }}>
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={{ xs: 6, md: 9 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.5 }}>About DevCraft</Typography>
              <Typography component="h1" sx={{ mt: 1.5, maxWidth: 760, fontSize: { xs: '2.65rem', sm: '3.5rem', md: '4.35rem' }, lineHeight: 1.04, fontWeight: 850, letterSpacing: '-0.055em', color: 'text.primary' }}>
                Technology should make business <Box component="span" sx={{ color: 'primary.main' }}>feel simpler.</Box>
              </Typography>
              <Typography sx={{ mt: 3, maxWidth: 680, color: 'text.secondary', fontSize: { xs: '1.08rem', md: '1.22rem' }, lineHeight: 1.75 }}>
                We help growing businesses remove repetitive work, replace disconnected processes, and create digital experiences that earn trust.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ bgcolor: '#082F49', color: 'white', borderRadius: 4, p: { xs: 3, md: 4 }, boxShadow: '0 28px 80px rgba(8,47,73,0.18)' }}>
                <Typography variant="overline" sx={{ color: '#84E4E1', fontWeight: 800, letterSpacing: 1.4 }}>Our point of view</Typography>
                <Typography sx={{ mt: 1.5, fontSize: { xs: '1.5rem', md: '1.8rem' }, lineHeight: 1.35, fontWeight: 760, letterSpacing: '-0.025em' }}>Useful beats impressive. Clear beats complicated.</Typography>
                <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.67)', lineHeight: 1.75 }}>The best solution is the one your team understands, your customers enjoy, and your business can keep improving.</Typography>
                <Stack spacing={1.2} sx={{ mt: 3 }}>
                  {['Business-first thinking', 'Thoughtful execution', 'Long-term maintainability'].map((item) => (
                    <Stack key={item} direction="row" spacing={1} alignItems="center"><CheckCircleRoundedIcon sx={{ color: '#50CFCA', fontSize: 19 }} /><Typography fontWeight={650} fontSize="0.92rem">{item}</Typography></Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 9, md: 13 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.4 }}>Why we exist</Typography>
              <Typography component="h2" sx={{ mt: 1, fontSize: { xs: '2.1rem', md: '3rem' }, lineHeight: 1.12, fontWeight: 820, letterSpacing: '-0.045em' }}>A focused partner for meaningful digital work.</Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography sx={{ color: 'text.secondary', fontSize: { xs: '1.08rem', md: '1.18rem' }, lineHeight: 1.85 }}>DevCraft combines software engineering, automation, and design to solve practical business problems. We work best with teams that want progress without unnecessary layers, jargon, or oversized systems.</Typography>
              <Typography sx={{ mt: 2.5, color: 'text.secondary', fontSize: { xs: '1.08rem', md: '1.18rem' }, lineHeight: 1.85 }}>From the first conversation to launch, our job is to make the path clear: understand what matters, design the right experience, build it carefully, and leave you with something dependable.</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 9, md: 12 }, bgcolor: '#EEF7F8' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 700, mb: { xs: 5, md: 7 } }}>
            <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.4 }}>How we show up</Typography>
            <Typography component="h2" sx={{ mt: 1, fontSize: { xs: '2.1rem', md: '3.05rem' }, lineHeight: 1.12, fontWeight: 820, letterSpacing: '-0.045em' }}>Small-team attention. Serious delivery standards.</Typography>
          </Box>
          <Grid container spacing={3}>
            {principles.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Card elevation={0} sx={{ height: '100%', p: { xs: 3, md: 3.5 }, borderRadius: 3, border: '1px solid rgba(16,42,58,0.1)', transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 22px 48px rgba(16,42,58,0.09)', borderColor: 'rgba(32,184,181,0.45)' } }}>
                  <Box sx={{ width: 48, height: 48, display: 'grid', placeItems: 'center', color: 'primary.main', bgcolor: 'secondary.light', borderRadius: 2, mb: 3 }}>{item.icon}</Box>
                  <Typography component="h3" sx={{ fontSize: '1.4rem', fontWeight: 780, letterSpacing: '-0.02em' }}>{item.title}</Typography>
                  <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.75 }}>{item.text}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 9, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.4 }}>What we bring together</Typography>
              <Typography component="h2" sx={{ mt: 1, fontSize: { xs: '2.1rem', md: '3rem' }, lineHeight: 1.12, fontWeight: 820, letterSpacing: '-0.045em' }}>Strategy, design, and engineering in one conversation.</Typography>
              <Typography sx={{ mt: 2.5, color: 'text.secondary', fontSize: '1.06rem', lineHeight: 1.75 }}>You do not need to translate between separate teams. We connect the business goal to the experience and the technical decisions behind it.</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                {capabilities.map((item) => (
                  <Stack key={item} direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, border: '1px solid rgba(16,42,58,0.1)', borderRadius: 2.5, bgcolor: '#FBFDFD' }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: 'secondary.light', flexShrink: 0 }}><CheckCircleRoundedIcon sx={{ color: 'secondary.dark', fontSize: 19 }} /></Box>
                    <Typography fontWeight={690}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: '#082F49', color: 'white', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={{ color: '#84E4E1', fontWeight: 800, letterSpacing: 1.4 }}>Start a conversation</Typography>
              <Typography component="h2" sx={{ mt: 1, fontSize: { xs: '2.1rem', md: '3rem' }, lineHeight: 1.12, fontWeight: 820, letterSpacing: '-0.045em' }}>Have a business problem worth simplifying?</Typography>
              <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.66)', fontSize: '1.06rem' }}>Tell us what is slowing you down or what you want to launch next.</Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
              <Button variant="contained" size="large" endIcon={<ArrowForwardRoundedIcon />} onClick={() => setDialogOpen(true)} sx={{ px: 3.2, py: 1.45, bgcolor: 'secondary.main', color: '#062D43', '&:hover': { bgcolor: '#50CFCA' } }}>Talk to DevCraft</Button>
              <Button component={RouterLink} to="/pricing" size="large" sx={{ ml: { sm: 1 }, mt: { xs: 1, sm: 0 }, color: 'white' }}>View solutions</Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <ContactDialog open={dialogOpen} onClose={() => setDialogOpen(false)} serviceTitle="General Inquiry" />
    </Box>
  );
};

export default About;
